import { Box, Stack, SxProps } from '@mui/material';
import { Button } from 'system/atoms';
import { Checkbox } from 'common/form';
import { Dropdown } from 'components/DropDown';
import { RadioGroup } from 'components/RadioGroup';
import { useEffect, useMemo, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { useMutation } from 'services/api/hooks';
import { updateCandidate } from 'services/candidate/api';
import { statusSetAtom } from 'services/candidate/atoms';
import { CandidateStatus } from 'services/candidate/types';
import { getStatusOptions } from 'services/candidate/utils/getStatusOptions';
import { integrationAtom } from 'services/integration/atom';
import {
  useInterviewStages,
  useRejectedReasons
} from 'services/integration/hooks';
import { isInterviewStageBasedRejection } from 'services/integration/utils';

const buttonsContainer: SxProps = {
  display: 'flex',
  gap: 2,
  justifyContent: 'space-between'
};

type Props = {
  defaultLockStatus: boolean;
  candidateIds: string[];
  atsJobId: string | null | undefined;
  defaultStatus?: CandidateStatus;
  onCancel: () => void;
  onClearSelectedCandidates?: () => void;
};

export const UpdateCandidateStatus: React.FC<Props> = ({
  defaultLockStatus,
  candidateIds,
  atsJobId,
  defaultStatus = CandidateStatus.SHORT_LISTED,
  onCancel,
  onClearSelectedCandidates
}) => {
  const [lockStatus, setLockStatus] = useState(defaultLockStatus);
  const [selectedStatus, setSelectedStatus] =
    useState<CandidateStatus>(defaultStatus);

  const [selectedRejectedReasonId, setSelectedRejectedReasonId] = useState<
    string | undefined
  >(undefined);

  const [selectedInterviewStageId, setSelectedInterviewStageId] = useState<
    string | undefined
  >(undefined);

  const { rejectedReasons } = useRejectedReasons({ atsJobId });

  const { interviewStages } = useInterviewStages({ atsJobId });

  const integration = useRecoilValue(integrationAtom);
  const useStagesForRejection = isInterviewStageBasedRejection(integration);

  const statusSet = useRecoilValue(statusSetAtom);

  const { mutate, isLoading } = useMutation(updateCandidate, {
    successTextMessage: 'Candidate(s) status updated successfully.',
    errorTextMessage: 'Failed to update candidate status.'
  });

  const updateCandidatesStatus = async () => {
    const selectedStage = interviewStages.find(
      (s) => s.id === selectedInterviewStageId
    );
    const selectedReason = rejectedReasons.find(
      (r) => r.id === selectedRejectedReasonId
    );

    await Promise.all(
      candidateIds.map((candidateId) =>
        mutate({
          id: candidateId,
          status: selectedStatus,
          // reset to null if no status is selected
          atsRejectedReasonId:
            selectedReason?.remoteId || selectedRejectedReasonId || null,
          atsInterviewStageId:
            selectedStage?.remoteId || selectedInterviewStageId || null,
          komboStageId: selectedStage?.remoteId
            ? selectedInterviewStageId
            : null,
          komboRejectionReasonId: selectedReason?.remoteId
            ? selectedRejectedReasonId
            : null,
          isLocked: lockStatus ? true : undefined
        })
      )
    );
    onClearSelectedCandidates?.();
  };

  const { atsUpdateOptions, atsUpdateAction, atsUpdateValue, placeholder } =
    useMemo(() => {
      if (
        selectedStatus === CandidateStatus.REJECTED &&
        !useStagesForRejection
      ) {
        return {
          atsUpdateOptions: rejectedReasons.map((reason) => ({
            label: reason.label,
            value: reason.id
          })),
          atsUpdateAction: setSelectedRejectedReasonId,
          atsUpdateValue: selectedRejectedReasonId,
          placeholder: 'Select a rejection reason'
        };
      }

      return {
        atsUpdateOptions: interviewStages.map((stage) => ({
          label: stage.name,
          value: stage.id
        })),
        atsUpdateAction: setSelectedInterviewStageId,
        atsUpdateValue: selectedInterviewStageId,
        placeholder: 'Select an interview stage'
      };
    }, [
      interviewStages,
      rejectedReasons,
      selectedRejectedReasonId,
      selectedInterviewStageId,
      selectedStatus,
      useStagesForRejection
    ]);

  useEffect(() => {
    if (selectedInterviewStageId && selectedRejectedReasonId) {
      if (
        selectedStatus === CandidateStatus.REJECTED &&
        !useStagesForRejection
      ) {
        setSelectedInterviewStageId(undefined);
      } else {
        setSelectedRejectedReasonId(undefined);
      }
    }
  }, [
    selectedInterviewStageId,
    selectedRejectedReasonId,
    selectedStatus,
    useStagesForRejection
  ]);

  return (
    <Stack gap="24px">
      <RadioGroup
        label="Select a status"
        options={getStatusOptions({ statusSet })}
        value={selectedStatus}
        onChange={(e, value) => {
          setSelectedStatus(value as CandidateStatus);
        }}
      />

      {atsUpdateOptions.length > 0 && (
        <Box mt={2}>
          <Dropdown
            options={atsUpdateOptions}
            onChange={(value) => atsUpdateAction(value)}
            value={atsUpdateValue || ''}
            label="Update your ATS"
            placeholder={placeholder}
          />
        </Box>
      )}

      <Checkbox
        value={lockStatus}
        onChange={() => setLockStatus(!lockStatus)}
        field={{
          checkboxLabel: 'Lock this status'
        }}
      />

      <Box sx={buttonsContainer}>
        <Button
          variant="primary"
          fullWidth
          onClick={updateCandidatesStatus}
          disabled={isLoading}
        >
          Confirm
        </Button>
        <Button
          variant="tertiary"
          state="destructive"
          fullWidth
          onClick={onCancel}
        >
          Cancel
        </Button>
      </Box>
    </Stack>
  );
};
